import { useState } from "react";
import { ChevronDown, TrendingUp, TrendingDown, Minus, MessageSquarePlus } from "lucide-react";
import { entityNameOf, timeframeOf, TIMEFRAME_LABELS } from "../lib/findingCompat";

const HORIZONS = ["this_week", "this_month", "this_quarter"];

const accent = (change) => {
  if (change === "up")   return "bg-emerald-500";
  if (change === "down") return "bg-rose-500";
  return "bg-slate-300";
};

const badge = (change) => {
  if (change === "up")   return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (change === "down") return "text-rose-700 bg-rose-50 border-rose-200";
  return "text-slate-600 bg-slate-50 border-slate-200";
};

export default function BriefingSection({
  section,
  index,
  defaultOpen = false,
  onAskAgent,
  readOnly = false,
  forceOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const findings = section.findings || [];
  const tables = section.tables || [];
  const isOpen = forceOpen || open;

  const body = (
    <div className="px-6 pb-6 pt-1">
      {section.summary && (
        <p className="mb-5 border-l-2 border-slate-200 pl-3 text-xs leading-relaxed text-slate-500 italic">
          {section.summary}
        </p>
      )}

      {tables.length > 0 && (
        <div className="space-y-5 mb-2">
          {tables.map((t, i) => <SectionTable key={i} table={t} />)}
        </div>
      )}

      {section.key === "next_steps" ? (
        <NextSteps findings={findings} onAskAgent={onAskAgent} section={section} readOnly={readOnly} />
      ) : findings.length === 0 ? (
        tables.length === 0 && <p className="text-sm text-slate-400">No findings.</p>
      ) : (
        <ul className={`space-y-0.5 ${tables.length > 0 ? "mt-5" : ""}`}>
          {findings.map((f, i) => (
            <FindingRow
              key={i}
              finding={f}
              sectionKey={section.key}
              index={i}
              onAskAgent={onAskAgent}
              section={section}
              readOnly={readOnly}
            />
          ))}
        </ul>
      )}

      {section.data_limitations && (
        <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          <strong className="font-semibold">Data note:</strong> {section.data_limitations}
        </div>
      )}
    </div>
  );

  return (
    <div
      id={`section-${section.key}`}
      className="card-soft overflow-hidden"
      data-testid={`briefing-section-${section.key}`}
    >
      {forceOpen ? (
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-2.5">
            <span className="num text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              0{index + 1}
            </span>
            <h3 className="font-heading text-xl font-medium text-slate-900">{section.title}</h3>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-slate-50"
          data-testid={`briefing-section-toggle-${section.key}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="num text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                0{index + 1}
              </span>
              <h3 className="font-heading text-xl font-medium text-slate-900">{section.title}</h3>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{section.summary}</p>
            {!open && (tables.length > 0 || findings.length > 0) && (
              <p className="mt-3 text-xs font-medium text-slate-400">
                {tables.length > 0 ? `${tables.length} table${tables.length > 1 ? "s" : ""} · ` : ""}
                {findings.length} finding{findings.length === 1 ? "" : "s"} · click to expand
              </p>
            )}
          </div>
          <ChevronDown
            size={18}
            strokeWidth={1.8}
            className={`mt-2 flex-shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      )}

      {isOpen && body}
    </div>
  );
}

function SectionTable({ table }) {
  const [filter, setFilter] = useState("all");
  const cols = table.headers.length;
  const gridCols = `2fr ${Array(cols - 1).fill("1fr").join(" ")}`;

  const rb = table.row_branded;
  const hasToggle = rb && rb.some(b => b === true || b === false);
  const visibleRows = hasToggle && filter !== "all"
    ? table.rows.filter((_, i) => rb[i] == null || rb[i] === (filter === "branded"))
    : table.rows;
  const visibleRb = hasToggle && filter !== "all"
    ? rb.filter((b, i) => b == null || b === (filter === "branded"))
    : rb;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      {(table.label || hasToggle) && (
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2">
          {table.label && (
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{table.label}</span>
          )}
          {hasToggle && (
            <div className="flex items-center gap-1">
              {["all", "branded", "non-branded"].map(opt => (
                <button
                  key={opt}
                  onClick={() => setFilter(opt)}
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors ${
                    filter === opt ? "bg-[#0052FF] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {opt === "all" ? "All" : opt === "branded" ? "Branded" : "Non-Branded"}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div
        className="grid gap-4 bg-slate-50 px-4 py-2.5"
        style={{ gridTemplateColumns: gridCols }}
      >
        {table.headers.map((h, i) => (
          <span
            key={i}
            className={`text-[10px] font-semibold uppercase tracking-wider text-slate-400 ${i > 0 ? "text-right" : ""}`}
          >
            {h}
          </span>
        ))}
      </div>
      {visibleRows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-400">No rows.</p>
      ) : (
        visibleRows.map((row, i) => (
          <div
            key={i}
            className={`grid gap-4 border-t border-slate-100 px-4 py-3 ${i % 2 === 1 ? "bg-slate-50/50" : "bg-white"}`}
            style={{ gridTemplateColumns: gridCols }}
          >
            {row.map((cell, j) => (
              <span
                key={j}
                title={j === 0 ? cell : undefined}
                className={`text-sm ${j === 0 ? "truncate font-medium text-slate-900" : "num text-right text-slate-600"}`}
              >
                {cell}
              </span>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function FindingRow({ finding: f, sectionKey, index, onAskAgent, section, readOnly }) {
  return (
    <li
      className="group flex items-start gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-slate-50"
      data-testid={`finding-${sectionKey}-${index}`}
    >
      <div className={`mt-1.5 h-3 w-0.5 flex-shrink-0 rounded-full ${accent(f.change)}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold leading-snug text-slate-900">{f.title}</span>
              {f.metric && (
                <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge(f.change)}`}>
                  {f.metric}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{f.detail}</p>
            {entityNameOf(f) && (
              <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-slate-400">{entityNameOf(f)}</p>
            )}
          </div>
          {!readOnly && (
            <button
              onClick={() => onAskAgent && onAskAgent(section, f)}
              className="flex-shrink-0 rounded-md border border-slate-200 bg-white p-1.5 text-slate-400 opacity-0 transition-opacity hover:border-[#0052FF] hover:text-[#0052FF] group-hover:opacity-100"
              data-testid={`ask-agent-btn-${sectionKey}-${index}`}
            >
              <MessageSquarePlus size={12} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function NextSteps({ findings, onAskAgent, section, readOnly }) {
  if (!findings.length) return <p className="text-sm text-slate-400">No actions generated.</p>;

  const grouped = HORIZONS.reduce((acc, h) => {
    acc[h] = findings.filter((f) => timeframeOf(f) === h);
    return acc;
  }, {});
  const ungrouped = findings.filter((f) => !HORIZONS.includes(timeframeOf(f)));

  const horizonStyle = {
    this_week:    { dot: "bg-rose-500",    pill: "text-rose-700 bg-rose-50 border-rose-200" },
    this_month:   { dot: "bg-amber-500",   pill: "text-amber-700 bg-amber-50 border-amber-200" },
    this_quarter: { dot: "bg-emerald-500", pill: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  };

  return (
    <div className="space-y-6">
      {HORIZONS.map((horizon) => {
        const items = grouped[horizon];
        if (!items?.length) return null;
        const s = horizonStyle[horizon];
        return (
          <div key={horizon}>
            <div className={`mb-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 ${s.pill}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
              <span className="text-[11px] font-semibold uppercase tracking-wider">{TIMEFRAME_LABELS[horizon]}</span>
            </div>
            <ul className="space-y-0.5">
              {items.map((f, i) => (
                <li key={i} className="group flex items-start gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-slate-50">
                  <span className={`mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full ${s.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{f.title}</span>
                          {f.metric && (
                            <span className="text-[11px] font-medium text-slate-500">{f.metric}</span>
                          )}
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">{f.detail}</p>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => onAskAgent && onAskAgent(section, f)}
                          className="flex-shrink-0 rounded-md border border-slate-200 bg-white p-1.5 text-slate-400 opacity-0 transition-opacity hover:border-[#0052FF] hover:text-[#0052FF] group-hover:opacity-100"
                        >
                          <MessageSquarePlus size={12} strokeWidth={1.8} />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {ungrouped.length > 0 && (
        <ul className="space-y-0.5">
          {ungrouped.map((f, i) => (
            <FindingRow key={i} finding={f} sectionKey="next_steps" index={i} onAskAgent={onAskAgent} section={section} readOnly={readOnly} />
          ))}
        </ul>
      )}
    </div>
  );
}
